import React, { useState, useEffect } from "react";
import { X, ShieldAlert } from "lucide-react";
import useStudentActivities from "../../../../hooks/professor/useStudentActivities";
import useActivityFilters from "../../../../hooks/professor/useActivityFilters";
import useActivityReactions from "../../../../hooks/professor/useActivityReactions";
import useBulkActions from "../../../../hooks/professor/useBulkActions";
import ActivityFilters from "../filters/ActivityFilters";
import BulkActionBar from "../actions/BulkActionBar";
import ActivityList from "../list/ActivityList";
import Pagination from "./Pagination";
import GradeActivityModal from "./GradeActivityModal";
import ImageViewModal from "./ImageViewModal";
import DocumentPreviewModal from "../documents/DocumentPreviewModal";
import { fetchProfessorSignature } from "../../../../services/professorActivityService";

const StudentActivityModal = ({ student, onClose, user, fromSource = "Professor" }) => {
  const [professorSignature, setProfessorSignature] = useState(null);
  const [gradingActivity, setGradingActivity] = useState(null);
  const [initialDecision, setInitialDecision] = useState("Approved");
  const [modalSource, setModalSource] = useState(fromSource || "Professor");
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    if (fromSource) {
      setModalSource(fromSource);
    }
  }, [fromSource]);

  const handleOpenGradeModal = (act, dec = "Approved", src) => {
    setGradingActivity(act);
    setInitialDecision(dec);
    setModalSource(src || fromSource || "Professor");
  };

  // Hook 1: Fetch Student Activities
  const {
    activities,
    setActivities,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    updateFilters,
    refreshActivities,
  } = useStudentActivities(student?.id);

  // Hook 2: Activity Filters
  const {
    activityTypes,
    selectedType,
    setSelectedType,
    selectedStatus,
    setSelectedStatus,
    selectedRatingStatus,
    setSelectedRatingStatus,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    resetFilters,
  } = useActivityFilters(activities);

  // Hook 3: Activity Reactions
  const { processingReaction, addReaction, addBulkReactions } = useActivityReactions(
    user,
    { signature: professorSignature }
  );

  // Hook 4: Bulk Actions
  const {
    selectedActivities,
    selectAllChecked,
    handleActivitySelection,
    handleSelectAllToggle,
    clearSelection,
    selectedCount,
  } = useBulkActions(activities);

  // Fetch signature on mount
  useEffect(() => {
    fetchProfessorSignature().then((sig) => setProfessorSignature(sig));
  }, []);

  // Pass active filters to useStudentActivities
  useEffect(() => {
    updateFilters({
      type: selectedType,
      status: selectedStatus,
      date_from: dateFrom,
      date_to: dateTo,
    });
  }, [selectedType, selectedStatus, dateFrom, dateTo]);

  if (!student) return null;

  const handleSingleReaction = async (act, type) => {
    const res = await addReaction(act, type, activities, setActivities);
    if (res.success) {
      refreshActivities();
    }
  };

  const handleBulkApprove = async () => {
    if (selectedActivities.length === 0) return;
    const res = await addBulkReactions(selectedActivities, "✅", activities, setActivities);
    if (res.success) {
      clearSelection();
      refreshActivities();
    }
  };

  const handleBulkReject = async () => {
    if (selectedActivities.length === 0) return;
    const res = await addBulkReactions(selectedActivities, "❌", activities, setActivities);
    if (res.success) {
      clearSelection();
      refreshActivities();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl w-full max-w-[95vw] xl:max-w-7xl h-[92vh] shadow-2xl flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
              {student.name ? student.name.charAt(0).toUpperCase() : "S"}
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-lg leading-snug flex items-center gap-2">
                <span>{student.name || student.username}</span>
                <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-extrabold text-xs">
                  {student.course || "MBBS"}
                </span>
                {fromSource !== "Principal" && fromSource !== "ReadOnly" && (
                  <span className={`px-3 py-0.5 rounded-full text-xs font-bold border ${
                    fromSource === "HOD" ? "bg-purple-100 text-purple-900 border-purple-300" : "bg-blue-50 text-blue-900 border-blue-200"
                  }`}>
                    {fromSource === "HOD" ? "🏛️ HOD Portal Mode" : "👨‍🏫 Faculty Portal Mode"}
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                {student.department ? `${student.department} Department • ` : ""}
                Student ID: {student.student_id || student.username} • Total Logged Activities: {totalCount}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 p-6 overflow-y-auto bg-slate-100/50">
          {/* Authorization error banner */}
          {error && error.includes("Access denied") && (
            <div className="mb-4 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-3 shadow-sm">
              <ShieldAlert className="w-5 h-5 text-rose-600 flex-shrink-0" />
              <div>
                <strong className="font-bold block text-sm">Authorization Restriction</strong>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Filters */}
          <ActivityFilters
            activityTypes={activityTypes}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedRatingStatus={selectedRatingStatus}
            setSelectedRatingStatus={setSelectedRatingStatus}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            resetFilters={resetFilters}
          />

          {/* Bulk Action Bar */}
          {fromSource !== "Principal" && fromSource !== "ReadOnly" && (
            <BulkActionBar
              selectedCount={selectedCount}
              totalCount={activities.length}
              selectAllChecked={selectAllChecked}
              onSelectAllToggle={handleSelectAllToggle}
              onBulkApprove={handleBulkApprove}
              onBulkReject={handleBulkReject}
              processing={processingReaction}
            />
          )}

          {/* Activities List */}
          <ActivityList
            activities={activities}
            loading={loading}
            error={error}
            selectedActivities={selectedActivities}
            onSelectToggle={handleActivitySelection}
            onReaction={(act, dec, src) => handleOpenGradeModal(act, dec, src || fromSource)}
            onGradeClick={(act, dec, src) => handleOpenGradeModal(act, dec, src || fromSource)}
            onImageClick={(img) => setViewingImage(img)}
            onDocView={(doc) => setViewingDoc(doc)}
            processing={processingReaction}
            currentUserEmail={user?.email}
            fromSource={fromSource}
          />
        </div>

        {/* Pagination Footer */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setCurrentPage(p)}
        />
      </div>

      {/* Grade Activity Modal */}
      {gradingActivity && (
        <GradeActivityModal
          activity={gradingActivity}
          initialDecision={initialDecision}
          fromSource={modalSource}
          onClose={() => setGradingActivity(null)}
          onSubmitted={() => refreshActivities()}
          professorProfile={{ signature: professorSignature, email: user?.email }}
        />
      )}

      {/* Image Preview Modal */}
      {viewingImage && (
        <ImageViewModal imageUrl={viewingImage} onClose={() => setViewingImage(null)} />
      )}

      {/* Document Preview Modal */}
      {viewingDoc && (
        <DocumentPreviewModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />
      )}
    </div>
  );
};

export default StudentActivityModal;
